'use client';

import { Card } from '../../components/card';
import { Text } from '../../components/text';
import { MonoDefault } from '../../components/typography';
import { Badge } from '../../components/badge';
import { MarkdownPreview } from '..';
import { Icon } from '../../components/icon';
import { SignalSummary } from './SignalSummary';
import { LinkedFindingsSection } from './LinkedFindingsSection';
import { StructuredPoc } from './StructuredPoc';
import { parsePocBlocksTolerant } from '@terra/shared-schemas-orchestrator';
import { Grid, GridItem, HStack, VStack } from '@terra/ui-styled-system';
import {
  BrainIcon,
  Calendar03Icon,
  Tick01Icon,
  CircuitBoardIcon,
  SourceCodeIcon,
  Copy01Icon,
  File01Icon,
  FolderGitIcon,
  CheckListIcon,
  Loading03Icon,
  MicroscopeIcon,
  SearchAddIcon,
  Shield01Icon,
  StarIcon,
  Cancel01Icon,
  type TerraIcon,
} from '@terra/ui-icons';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { FindingSeverityBadge } from './FindingSeverityBadge';
import { CVSSWidget } from './CVSSWidget';
import type { InternalEnrichedFinding } from '../../types';
import { useTranslations } from 'next-intl';
import { Button } from '../../components/button';
import { LegacyDialog } from '../../components/dialog';
import { IconButton } from '../../components/icon-button';
import { Clipboard } from '../../components/clipboard';
import { formatCategoryForDisplay } from '../../utils';
import { FindingAssetsDisplay } from './FindingAssetsDisplay';

interface FindingDetailsProps {
  finding: InternalEnrichedFinding;
  dateFormatter: {
    fromString: (date: string) => string;
  };
  mode?: 'web' | 'ext';
  shouldShowSignal?: boolean;
  linkedFindings?: InternalEnrichedFinding[];
  onNavigateToFinding?: (findingId: string) => void;
}

export function FindingDetails({
  finding,
  dateFormatter,
  mode = 'web',
  shouldShowSignal,
  linkedFindings,
  onNavigateToFinding,
}: FindingDetailsProps) {
  const t = useTranslations('designSystem.findings.findingDetails');
  const [showExploitationGuide, setShowExploitationGuide] = useState(false);
  const [showExecutionScript, setShowExecutionScript] = useState(false);
  const tolerantPocBlocks = finding.pocBlocks
    ? parsePocBlocksTolerant(finding.pocBlocks)
    : [];
  const pocBlocks = tolerantPocBlocks.length > 0 ? tolerantPocBlocks : null;

  return (
    <>
      <Grid gap={4} w="full">
        <GridItem>
          <Card.Root w="full">
            <Card.Header>
              <HStack w="full" justifyContent="start">
                <FindingSeverityBadge finding={finding} size="sm" />
                {finding.category && (
                  <Badge variant="outline">
                    {formatCategoryForDisplay(finding.category)}
                  </Badge>
                )}
                <Text fontWeight="bold" fontSize="2xl">
                  {finding.title}
                </Text>
                <HStack marginInlineStart="auto">
                  <Calendar03Icon />
                  {dateFormatter.fromString(finding?.discoveryDate.toString())}
                </HStack>
              </HStack>
            </Card.Header>
          </Card.Root>
        </GridItem>

        {(finding.cvss?.score ||
          finding.cvss?.vector ||
          finding.cvss?.environmentalScore ||
          finding.cvss?.environmentalVector) && (
          <GridItem>
            <StyledCard title={t('score')} icon={StarIcon}>
              <CVSSWidget finding={finding} />
            </StyledCard>
          </GridItem>
        )}

        <GridItem>
          <Grid gap={4} w="full" columns={2}>
            <GridItem>
              <StyledCard
                title={t('description')}
                markdown={finding.description ?? undefined}
                icon={File01Icon}
              />
            </GridItem>
            <GridItem>
              <StyledCard
                title={t('businessImpact')}
                markdown={finding.businessImpact ?? undefined}
                icon={MicroscopeIcon}
              />
            </GridItem>
          </Grid>
        </GridItem>

        {shouldShowSignal && finding.signal && (
          <GridItem>
            <SignalSummary signal={finding.signal} />
          </GridItem>
        )}

        {linkedFindings && linkedFindings.length > 0 && (
          <GridItem>
            <LinkedFindingsSection
              findings={linkedFindings}
              onNavigateToFinding={onNavigateToFinding}
            />
          </GridItem>
        )}

        <GridItem>
          <Grid gap={4} w="full" columns={2}>
            <GridItem>
              <StyledCard
                title={t('poc')}
                markdown={pocBlocks ? undefined : finding.poc || undefined}
                icon={SearchAddIcon}
              >
                {pocBlocks ? (
                  <StructuredPoc blocks={pocBlocks} findingId={finding.id} />
                ) : !finding.poc ? (
                  <Text>{t('pocWillAppearHere')}</Text>
                ) : null}
              </StyledCard>
            </GridItem>
            <GridItem>
              <StyledCard
                title={t('mitigation')}
                markdown={finding.mitigationPlan || undefined}
                icon={Shield01Icon}
              >
                {!finding.mitigationPlan && (
                  <Text>{t('mitigationWillAppearHere')}</Text>
                )}
              </StyledCard>
            </GridItem>
          </Grid>
        </GridItem>

        <GridItem>
          {finding.remediationPlan ? (
            <StyledCard
              title={t('remediation')}
              badgeText={t('betaTag')}
              markdown={finding.remediationPlan}
              icon={CheckListIcon}
            />
          ) : (
            <StyledCard
              title={t('remediation')}
              badgeText={t('betaTag')}
              commingSoon
              icon={CheckListIcon}
            >
              <Text color="fg.muted">{t('remediationBetaNote')}</Text>
            </StyledCard>
          )}
        </GridItem>

        <GridItem>
          <Grid gap={4} w="full" columns={finding.filePath ? 2 : 1}>
            {finding.filePath ? (
              <GridItem>
                <StyledCard title={t('filePath')} icon={FolderGitIcon}>
                  <MonoDefault color="fg.default">
                    {finding.filePath}
                  </MonoDefault>
                </StyledCard>
              </GridItem>
            ) : null}
            <GridItem>
              <StyledCard title={t('assets')} icon={CircuitBoardIcon}>
                {finding.assets.length > 0 ? (
                  <FindingAssetsDisplay assets={finding.assets} />
                ) : (
                  <Text
                    color="fg.muted"
                    fontSize="sm"
                    textAlign="center"
                    py={4}
                  >
                    {t('relatedAssetWillAppearHere')}
                  </Text>
                )}
              </StyledCard>
            </GridItem>
          </Grid>
        </GridItem>

        {/* Exploitation Guide Section */}
        {mode === 'ext' &&
          (finding.exploitationGuide || finding.executionScript) && (
            <GridItem>
              <Card.Root h="full" w="full" minHeight="150px">
                <Card.Header>
                  <HStack gap={2}>
                    <Icon size="sm">
                      <Shield01Icon />
                    </Icon>
                    <Text fontWeight="bold">{t('exploitationGuide')}</Text>
                  </HStack>
                </Card.Header>
                <Card.Body>
                  <HStack gap={3} justifyContent="flex-start">
                    {finding.exploitationGuide && (
                      <Button
                        variant="outline"
                        onClick={() => setShowExploitationGuide(true)}
                        size="sm"
                      >
                        <Icon size="sm" mr={2}>
                          <BrainIcon />
                        </Icon>
                        {t('exploitationGuide')}
                      </Button>
                    )}
                    {finding.executionScript && (
                      <Button
                        variant="outline"
                        onClick={() => setShowExecutionScript(true)}
                        size="sm"
                      >
                        <Icon size="sm" mr={2}>
                          <SourceCodeIcon />
                        </Icon>
                        {t('executionScript')}
                      </Button>
                    )}
                  </HStack>
                </Card.Body>
              </Card.Root>
            </GridItem>
          )}
      </Grid>

      {/* Exploitation Guide Modal */}
      <LegacyDialog.Root
        open={showExploitationGuide}
        onOpenChange={({ open }) => setShowExploitationGuide(open)}
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxWidth="800px" maxHeight="90vh">
            <VStack gap="0" alignItems="stretch" height="full">
              <HStack
                justifyContent="space-between"
                alignItems="center"
                p="6"
                pb="4"
                flexShrink={0}
              >
                <LegacyDialog.Title>
                  {t('exploitationGuide')}
                </LegacyDialog.Title>
                <HStack gap="2">
                  <Clipboard.Root value={finding.exploitationGuide || ''}>
                    <Clipboard.Trigger asChild>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label="Copy exploitation guide"
                      >
                        <Clipboard.Indicator copied={<Tick01Icon size={16} />}>
                          <Copy01Icon size={16} />
                        </Clipboard.Indicator>
                      </IconButton>
                    </Clipboard.Trigger>
                  </Clipboard.Root>
                  <LegacyDialog.CloseTrigger asChild>
                    <IconButton variant="ghost" size="sm" aria-label="Close">
                      <Cancel01Icon size={16} />
                    </IconButton>
                  </LegacyDialog.CloseTrigger>
                </HStack>
              </HStack>
              <LegacyDialog.Description
                px="6"
                pb="6"
                overflowY="auto"
                flex="1"
                css={{
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'var(--colors-bg-muted)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'var(--colors-border-default)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: 'var(--colors-border-emphasized)',
                  },
                }}
              >
                <MarkdownPreview source={finding.exploitationGuide || ''} />
              </LegacyDialog.Description>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>

      {/* Execution Script Modal */}
      <LegacyDialog.Root
        open={showExecutionScript}
        onOpenChange={({ open }) => setShowExecutionScript(open)}
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxWidth="800px" maxHeight="90vh">
            <VStack gap="0" alignItems="stretch" height="full">
              <HStack
                justifyContent="space-between"
                alignItems="center"
                p="6"
                pb="4"
                flexShrink={0}
              >
                <LegacyDialog.Title>{t('executionScript')}</LegacyDialog.Title>
                <HStack gap="2">
                  <Clipboard.Root value={finding.executionScript || ''}>
                    <Clipboard.Trigger asChild>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label="Copy execution script"
                      >
                        <Clipboard.Indicator copied={<Tick01Icon size={16} />}>
                          <Copy01Icon size={16} />
                        </Clipboard.Indicator>
                      </IconButton>
                    </Clipboard.Trigger>
                  </Clipboard.Root>
                  <LegacyDialog.CloseTrigger asChild>
                    <IconButton variant="ghost" size="sm" aria-label="Close">
                      <Cancel01Icon size={16} />
                    </IconButton>
                  </LegacyDialog.CloseTrigger>
                </HStack>
              </HStack>
              <LegacyDialog.Description px="6" pb="6" overflowY="auto" flex="1">
                <MarkdownPreview
                  source={`\`\`\`python\n${finding.executionScript || ''}\n\`\`\``}
                />
              </LegacyDialog.Description>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </>
  );
}

function StyledCard({
  title,
  markdown,
  children,
  commingSoon,
  icon: CardIcon,
  scrollable = true,
  bodyMaxHeight = '360px',
  badgeText,
  ...props
}: ComponentProps<typeof Card.Root> & {
  markdown?: string;
  commingSoon?: boolean;
  icon?: TerraIcon;
  scrollable?: boolean;
  bodyMaxHeight?: string | number;
  badgeText?: string;
}) {
  const t = useTranslations('designSystem.findings.findingDetails');

  return (
    <Card.Root
      h="full"
      w="full"
      minHeight="150px"
      display="flex"
      flexDirection="column"
      {...props}
    >
      <Card.Header>
        {commingSoon ? (
          <Text gap={1} display="inline-flex" alignItems="center">
            {CardIcon && (
              <Icon size="sm">
                <CardIcon />
              </Icon>
            )}
            <Text as="span" fontWeight="bold">
              {title}
            </Text>
            {badgeText && (
              <Badge size="sm" variant="emphasis" marginInlineStart={2}>
                {badgeText}
              </Badge>
            )}
            <Text
              as="span"
              fontSize="sm"
              marginInlineStart="auto"
              gap={2}
              display="flex"
              alignItems="center"
              color="gray.11"
            >
              <Text as="span" fontSize="xs">
                {t('comingSoon')}
              </Text>
              <Loading03Icon size={16} />
            </Text>
          </Text>
        ) : (
          <HStack gap={2}>
            {CardIcon && (
              <Icon size="sm">
                <CardIcon />
              </Icon>
            )}
            <Text fontWeight="bold">{title}</Text>
            {badgeText && (
              <Badge size="sm" variant="emphasis">
                {badgeText}
              </Badge>
            )}
          </HStack>
        )}
      </Card.Header>
      <Card.Body
        overflowY={scrollable ? 'auto' : undefined}
        maxHeight={scrollable ? bodyMaxHeight : undefined}
      >
        {markdown && <MarkdownPreview source={markdown} />}
        {children}
      </Card.Body>
    </Card.Root>
  );
}
